import { useMutation, useQuery } from '@apollo/client';
import { graphql } from '../../__generated__';
import { FulfillmentEvent } from '../../__generated__/graphql';
import { useTimezones } from '../../Common/hooks/useTimezones';
import { pendingOrderQuery } from './usePendingOrdersList';
import { pendingDispenseQuery } from './usePendingDispenseList';
import { showSuccessToast } from '../../Common/utils/toast';
import { shippedQuery } from '../views/Completed';

export const orderQuery = graphql(`
  query queryOrderModal($ID: ID) {
    fulfilment(id: $ID) {
      __typename
      id
      createdAt
      total
      subtotal
      dispensed
      shipping
      consults
      gateway
      completed
      booked
      discounts
      discountCodes
      items {
        QTY
        ID
        PrescriptionID
        __typename
      }
      events {
        event
        timestamp
      }
      street
      suburb
      state
      charged
      cancelled
      postcode
      total
      suburb
      prescriptionIDs
      creatingRole
      creatingRole
      deliveryMethod
      isPayInPerson
      customer {
        __typename
        id
        given
        dob
        gender
        brand
        mobile
        name
      }
    }
  }
`);

export const useOrder = (ID: string, closeModal: () => void) => {
  const { data, loading: loadingOrder } = useQuery(orderQuery, {
    variables: {
      ID: ID,
    },
  });

  const { convertUTCDateToUserISO } = useTimezones();

  const orderEvents =
    ([...(data?.fulfilment?.events ?? [])] as FulfillmentEvent[]).sort((a, b) => {
      return convertUTCDateToUserISO(a.timestamp)?.diff(convertUTCDateToUserISO(b.timestamp));
    }) ?? [];

  const [bookMutation_, { loading: booking }] = useMutation(
    graphql(`
      mutation bookDelivery($ID: ID) {
        bookFulfilment(id: $ID) {
          __typename
          id
          createdAt
          total
          subtotal
          dispensed
          shipping
          consults
          gateway
          completed
          booked
          discounts
          discountCodes
          items {
            QTY
            ID
            PrescriptionID
            __typename
          }
          events {
            event
            timestamp
          }
          street
          suburb
          state
          charged
          cancelled
          postcode
          total
          suburb
          prescriptionIDs
          creatingRole
          creatingRole
          deliveryMethod
          isPayInPerson
          customer {
            __typename
            id
            given
            dob
            gender
            brand
            mobile
            name
          }
        }
      }
    `),
    {
      refetchQueries: [
        {
          query: pendingDispenseQuery,
          fetchPolicy: 'network-only',
        },
        {
          query: shippedQuery,
          fetchPolicy: 'network-only',
          variables: { limit: 20, offset: 0 },
        },
      ],
    },
  );

  const [acceptOrder_, { loading: accepting }] = useMutation(
    graphql(`
      mutation processOrderManage($ID: ID, $dispense: Boolean) {
        completeFulfillmentOrder(orderID: $ID, dispense: $dispense) {
          ok
          error
          order {
            __typename
            id
            createdAt
            total
            subtotal
            dispensed
            shipping
            consults
            gateway
            completed
            booked
            discounts
            discountCodes
            items {
              QTY
              ID
              PrescriptionID
              __typename
            }
            events {
              event
              timestamp
            }
            street
            suburb
            state
            charged
            cancelled
            postcode
            total
            suburb
            prescriptionIDs
            creatingRole
            creatingRole
            deliveryMethod
            isPayInPerson
            customer {
              __typename
              id
              given
              dob
              gender
              brand
              mobile
              name
            }
          }
        }
      }
    `),
    {
      refetchQueries: [
        { query: orderQuery, variables: { ID: ID }, fetchPolicy: 'network-only' },
        {
          query: pendingOrderQuery,
          fetchPolicy: 'network-only',
        },
      ],
    },
  );

  const [dispenseOrder_, { loading: dispensing }] = useMutation(
    graphql(`
      mutation dispenseOrder($ID: ID) {
        dispenseFulfillmentOrder(orderID: $ID) {
          __typename
          id
          createdAt
          total
          subtotal
          dispensed
          shipping
          consults
          gateway
          completed
          booked
          discounts
          discountCodes
          items {
            QTY
            ID
            PrescriptionID
            __typename
          }
          events {
            event
            timestamp
          }
          street
          suburb
          state
          charged
          cancelled
          postcode
          total
          suburb
          prescriptionIDs
          creatingRole
          creatingRole
          deliveryMethod
          isPayInPerson
          customer {
            __typename
            id
            given
            dob
            gender
            brand
            mobile
            name
          }
        }
      }
    `),
    {
      refetchQueries: [
        { query: orderQuery, variables: { ID: ID }, fetchPolicy: 'network-only' },
        {
          query: pendingDispenseQuery,
          fetchPolicy: 'network-only',
        },
      ],
    },
  );

  const [cancelOrder_, { loading: cancelling }] = useMutation(
    graphql(`
      mutation cancelOrderManage($ID: ID) {
        cancelFufillmentOrder(id: $ID) {
          __typename
          id
          createdAt
          total
          subtotal
          dispensed
          shipping
          consults
          gateway
          completed
          booked
          discounts
          discountCodes
          items {
            QTY
            ID
            PrescriptionID
            __typename
          }
          events {
            event
            timestamp
          }
          street
          suburb
          state
          charged
          cancelled
          postcode
          total
          suburb
          prescriptionIDs
          creatingRole
          creatingRole
          deliveryMethod
          isPayInPerson
          customer {
            __typename
            id
            given
            dob
            gender
            brand
            mobile
            name
          }
        }
      }
    `),
    {
      refetchQueries: [
        { query: orderQuery, variables: { ID: ID }, fetchPolicy: 'network-only' },
        {
          query: pendingOrderQuery,
          fetchPolicy: 'network-only',
        },
      ],
    },
  );

  const cancelOrder = async () => {
    // @ts-ignore
    if (!confirm('Are you sure you wish to cancel this order?')) return;

    await cancelOrder_({
      variables: { ID },
    });

    showSuccessToast('Order cancelled - payment cancelled');

    closeModal();
  };

  const acceptOrder = async () => {
    // @ts-ignore
    if (!confirm('Are you sure you wish to accept this order?')) return;

    await acceptOrder_({
      variables: { ID, dispense: false },
    });
  };

  const dispenseOrder = async () => {
    await dispenseOrder_({
      variables: { ID },
    });
  };

  const bookOrder = async () => {
    await bookMutation_({
      variables: { ID },
    });
  };

  return {
    loading: loadingOrder || booking || dispensing || accepting || cancelling,
    order: data?.fulfilment,
    acceptOrder,
    dispenseOrder,
    orderEvents,
    bookOrder,
    cancelOrder,
  };
};
